import { IconDownload, IconFileCheck, IconLinkOff, IconRocket, IconShare } from '@tabler/icons-react';
import React, {
    useEffect, useMemo, useState, useCallback,
} from 'react';
import {
    NSButton, NSCard, NSSelect, NSSwitch,
} from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import {
    PRO_FORMA_TABS, PROJECT_ACTUALS_ICON_WITH_TOOLTIP_TEXT, LOADING_TEXT,
} from 'views/ProFormaTable/constants';
import { useQueryClient } from '@tanstack/react-query';
import useUpdateProForma from 'queries/ProForma/useUpdateProForma';
import { exportProFormaWorkbook } from 'views/ProFormaTable/helpers/excelExport/exportProFormaWorkbook';
import useToast from 'hooks/useToast';
import { IOptionAnyValue } from 'bricks/types';
import { useGetProjectBudgetScenarios } from 'queries/BudgetScenario/useGetProjectBudgetScenarios';
import { ISignUpManager } from 'views/public/SignUp/useSignUpManager';
import { useLocalStorage, GlobalKeys } from 'helpers/localStorage';
import UnlinkProjectModal from 'components/UnlinkProjectModal/UnlinkProjectModal';
import {
    getDefaultFooterKeyStats, getKeyStatsActualsDiscrepancies, getPartnerReturnFooterKeyStats, isProFormaKeyStatsInProgress,
} from 'views/ProFormaTable/helpers';
import { IKeyStatsCard, ISideNavFooterKeyStatDiscrepancy } from 'views/ProFormaTable/types';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import { URLS } from 'services/urls';
import { ProFormaModelIds } from 'constants/proForma';
import { useProFormaSocketContext } from '../../ProForma/socketContext/ProFormaSocketProvider';
import CreateProjectModal from '../CreateProjectModal/CreateProjectModal';
import SignUpSection from '../SignUpSection/SignUpSection';
import ProFormaSideNavSettingsMenu from './ProFormaSideNavSettingsMenu/ProFormaSideNavSettingsMenu';
import { ProFormaFooterKeyStatCard } from './ProFormaFooterKeyStatCard/ProFormaFooterKeyStatCard';
import ShareProFormaModal from '../ShareProFormaModal/ShareProFormaModal';

export interface IProFormaSideNavFooterProps {
    isCollapsed?: boolean;
    signUpMode?: boolean;
    currentTab?: string;
    setCurrentTab?: (tabName: string) => void;
    setDisplayManualSignUp?: (display: boolean) => void;
    signUpManager?: ISignUpManager;
}

const ProFormaSideNavFooter = ({
    isCollapsed, signUpMode = false,
    currentTab, setCurrentTab, setDisplayManualSignUp, signUpManager,
}: IProFormaSideNavFooterProps) => {
    const {
        proForma, shouldUseProjectActuals, setShouldUseProjectActuals, returnsData,
        showCents, updateProFormaLocal, waterfallAnalysis,
        projectActuals, disposition, uses, sources, operatingPeriodCashFlows, operatingInfo, permanentDebtsInfo,
        incomeSummary, residentialLotMixes, reportMetrics, salesSchedule, otherRevenue, sourceTypes,
    } = useProFormaSocketContext();
    const permanentDebtInfo = permanentDebtsInfo?.[0];
    const { showSuccess, showError } = useToast();
    const queryClient = useQueryClient();
    const { mutate: updateProForma } = useUpdateProForma();
    const [isExporting, setIsExporting] = useState(false);
    const [showPartnerReturns, setShowPartnerReturns] = useLocalStorage<boolean>(GlobalKeys.ShowPartnerReturns, false);
    const [proFormaExistingKeyStats, setProFormaExistingKeyStats] = useState<IKeyStatsCard[][]>([]);
    const [proFormaExistingPartnerReturnKeyStats, setProFormaExistingPartnerReturnKeyStats] = useState<IKeyStatsCard[][]>([]);

    const { data: budgetScenariosData, isLoading: isLoadingScenarios } = useGetProjectBudgetScenarios(
        proForma?.subprojectId?.toString() || '',
    );

    const scenarioOptions = budgetScenariosData?.budgetScenarios?.map(scenario => ({
        label: scenario.name,
        value: scenario.correlationId,
    })) || [];

    const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
    const [isUnlinkProjectModalOpen, setIsUnlinkProjectModalOpen] = useState(false);
    const [isShareProFormaModalOpen, setIsShareProFormaModalOpen] = useState(false);

    const persistedScenarioOption = proForma.projectBudgetScenarioId
        ? scenarioOptions.find(option => option.value === proForma.projectBudgetScenarioId)
        : undefined;
    const [selectedScenario, setSelectedScenario] = useState<{ label: string; value: string }>(
        persistedScenarioOption || (scenarioOptions[0] ?? { label: '', value: '' }),
    );

    const showCentsFormat = showCents ? '$0,0.00' : '$0,0';

    const returns = shouldUseProjectActuals && projectActuals ? projectActuals.returns : returnsData;
    const waterfallAnalysisData = shouldUseProjectActuals && projectActuals ? projectActuals.waterfallAnalysis : waterfallAnalysis;
    const projectOverviewUrl = budgetScenariosData?.subprojectId
        ? URLS.PROJECTS_APP.PROJECT_DASHBOARD_V2(budgetScenariosData.subprojectId)
        : undefined;

    // Check if required values are provided based on pro forma model type
    const isKeyStatsInProgress = useMemo(
        () => isProFormaKeyStatsInProgress(proForma, residentialLotMixes),
        [proForma, residentialLotMixes],
    );

    const getStatsInProgressIfRequired = useCallback((stats: IKeyStatsCard[][]) => {
        if (isKeyStatsInProgress) {
            return stats.map(row => row.map(stat => ({
                ...stat,
                value: null, // Enforces "In Progress" state
            })));
        }
        return stats;
    }, [isKeyStatsInProgress]);

    const proFormaKeyStats = useMemo(() => {
        const stats = getDefaultFooterKeyStats(returns, showCentsFormat, proForma?.proFormaModelId, reportMetrics);
        return getStatsInProgressIfRequired(stats);
    }, [
        returns,
        showCentsFormat,
        proForma?.proFormaModelId,
        reportMetrics,
        getStatsInProgressIfRequired,
    ]);

    const proFormaPartnerReturnKeyStats = useMemo(() => {
        const stats = getPartnerReturnFooterKeyStats(waterfallAnalysisData, showCentsFormat);
        return getStatsInProgressIfRequired(stats);
    }, [
        waterfallAnalysisData,
        showCentsFormat,
        getStatsInProgressIfRequired,
    ]);

    let keyStatsActualsDiscrepancies: Record<string, ISideNavFooterKeyStatDiscrepancy> = {};

    if (shouldUseProjectActuals && projectActuals) {
        keyStatsActualsDiscrepancies = getKeyStatsActualsDiscrepancies(
            proFormaExistingKeyStats.flat(),
            proFormaExistingPartnerReturnKeyStats.flat(),
            proFormaKeyStats.flat(),
            proFormaPartnerReturnKeyStats.flat(),
        );
    }

    const renderKeyStatRow = (statRow: any[], rowIndex: number | string) => (
        <div key={`stat-row-${rowIndex}`} className="d-flex">
            {statRow.map((stat, index) => ProFormaFooterKeyStatCard({
                index,
                stat,
                keyStatsActualsDiscrepancies,
                shouldAnimate: proForma.subprojectId == null,
            }))}
        </div>
    );

    const persistSelectedScenario = (scenarioCorrelationId: string, revertTo?: { label: string; value: string }) => {
        updateProForma({
            dealId: proForma.dealId,
            proFormaId: proForma.id,
            patchData: { projectBudgetScenarioId: scenarioCorrelationId },
        }, {
            onSuccess: () => {
                updateProFormaLocal({ projectBudgetScenarioId: scenarioCorrelationId });
                queryClient.invalidateQueries({ queryKey: ['proFormaData', proForma.dealId, proForma.id] });
                queryClient.invalidateQueries({ queryKey: ['getProFormas', proForma.dealId] });
            },
            onError: () => {
                if (revertTo) setSelectedScenario(revertTo);
                showError('Failed to save the scenario selection. Please try again.');
            },
        });
    };

    const handleSelectScenario = (option: IOptionAnyValue) => {
        if (option.value === selectedScenario.value) return;
        const previousScenario = selectedScenario;
        setSelectedScenario({ label: option.label, value: option.value });
        persistSelectedScenario(option.value, previousScenario);
    };

    const handleToggleUseProjectActuals = () => {
        const newValue = !shouldUseProjectActuals;
        setShouldUseProjectActuals(newValue);

        if (newValue && scenarioOptions.length > 0) {
            const persistedScenarioStillExists = scenarioOptions.find(option => option.value === proForma.projectBudgetScenarioId);
            if (!persistedScenarioStillExists) {
                const firstScenario = scenarioOptions[0];
                setSelectedScenario(firstScenario);
                persistSelectedScenario(firstScenario.value);
            }
        }
    };

    const handleToggleShowPartnerReturns = () => setShowPartnerReturns(!showPartnerReturns);

    const handleExportToExcel = async () => {
        setIsExporting(true);
        try {
            const saleMonth = operatingInfo?.saleMonth;
            const clampedCashFlows = saleMonth !== undefined
                ? operatingPeriodCashFlows.slice(0, saleMonth + 1)
                : operatingPeriodCashFlows;
            const { id: _permanentDebtId, ...permanentDebtData } = permanentDebtInfo || {};
            await exportProFormaWorkbook({
                proForma,
                operatingInfo: { ...operatingInfo, ...permanentDebtData },
                incomeSummary,
                usesSummary: uses,
                sourcesSummary: sources,
                cashFlows: clampedCashFlows,
                disposition,
                returns: returnsData,
                residentialLotMixes,
                otherRevenue,
                salesSchedule,
                sourceTypes,
                showCents,
                includeGainLossToLease: operatingInfo?.includeGainLossToLease ?? true,
            });
            showSuccess('Pro forma exported successfully');
        } catch {
            showError('Failed to export pro forma. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    // Cache pro forma stats when NOT in project actuals mode and data is loaded
    // This cache is used for discrepancy calculation when viewing project actuals
    useEffect(() => {
        const hasData = proFormaKeyStats.flat().some(stat => stat.value !== null && stat.value !== undefined);
        if (!shouldUseProjectActuals && hasData) {
            setProFormaExistingKeyStats(proFormaKeyStats);
            setProFormaExistingPartnerReturnKeyStats(proFormaPartnerReturnKeyStats);
        }
    }, [shouldUseProjectActuals, proFormaKeyStats, proFormaPartnerReturnKeyStats]);

    // Sync the dropdown when the persisted scenario or the options change (scenarios load
    // after the first render). Falls back to the first option when nothing is persisted or
    // the persisted scenario no longer exists — matching the backend's first-scenario fallback.
    // Intentionally not keyed on selectedScenario: re-running on local selection would snap
    // the dropdown back to the persisted value while the PATCH is still in flight.
    useEffect(() => {
        if (!scenarioOptions.length) return;
        const displayedScenarioOption = persistedScenarioOption ?? scenarioOptions[0];
        if (displayedScenarioOption.value !== selectedScenario.value) setSelectedScenario(displayedScenarioOption);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proForma.projectBudgetScenarioId, scenarioOptions.length]);

    const enabledForExport = [
        ProFormaModelIds.APARTMENT,
        ProFormaModelIds.APARTMENT_WITH_RETAIL,
        ProFormaModelIds.INDUSTRIAL,
        ProFormaModelIds.OFFICE,
        ProFormaModelIds.RETAIL,
        ProFormaModelIds.DATA_CENTER,
        ProFormaModelIds.STUDENT_HOUSING,
        ProFormaModelIds.LAND_DEVELOPMENT,
    ].includes(proForma.proFormaModelId);

    return (
        <>
            <NSCard className="NSCard--level-4 p-2 pt-1 mt-2 ProFormaSideNav__footer w-100">
                {signUpMode && !isCollapsed && setCurrentTab &&
                    setDisplayManualSignUp && signUpManager &&
                    currentTab === PRO_FORMA_TABS.GENERAL_INFORMATION.name && (
                    <>
                        <SignUpSection
                            setCurrentTab={setCurrentTab}
                            setDisplayManualSignUp={setDisplayManualSignUp}
                            signUpManager={signUpManager}
                        />
                        <hr className="mt-1" />
                    </>
                )}
                <RenderIf isTrue={proForma.subprojectId != null}>
                    <div className="ProFormaSideNav__footer__display-none d-flex justify-content-between mb-2">
                        <div className="d-flex">
                            <IconWithTooltip
                                name="project-actuals-icon"
                                icon={IconFileCheck}
                                tooltipText={PROJECT_ACTUALS_ICON_WITH_TOOLTIP_TEXT}
                                className="my-auto mr-1"
                            />
                            <span className="text-dark">Use project actuals</span>
                        </div>

                        <NSSwitch
                            id="useProjectActuals"
                            name="useProjectActuals"
                            checked={shouldUseProjectActuals}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleToggleUseProjectActuals()}
                            containerClassName="text-white heap-deal-pro-forma-show-project-actuals-toggle"
                        />

                    </div>
                    <NSCard
                        className="ProFormaSideNav__footer__display-none
                         p-2 mb-2 w-100 position-relative overflow-hidden font-13 NSCard--level-5"
                    >
                        <div>{budgetScenariosData?.developmentSiteName || LOADING_TEXT}</div>
                        <div className="text-dark font-weight-bold mt-1">
                            {projectOverviewUrl ? (
                                <a
                                    href={projectOverviewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-dark"
                                >
                                    {budgetScenariosData?.subprojectName || LOADING_TEXT}
                                </a>
                            ) : (
                                budgetScenariosData?.subprojectName || LOADING_TEXT
                            )}
                        </div>
                        <div className="mt-1">
                            <NSButton
                                id="unlink-from-project-btn"
                                ariaLabel="Unlink from project"
                                text="Unlink"
                                icon={IconLinkOff}
                                className="p-0 border-0 bg-transparent font-13 font-weight-normal heap-deal-pro-forma-unlink-from-project"
                                callback={() => setIsUnlinkProjectModalOpen(true)}
                            />
                        </div>
                        <RenderIf isTrue={shouldUseProjectActuals && budgetScenariosData?.isEarlyPlanning} isLoading={isLoadingScenarios}>
                            <NSSelect
                                name="projectScenarioSelect"
                                formClassName="pt-1"
                                options={scenarioOptions}
                                value={selectedScenario}
                                onChange={option => handleSelectScenario(option)}
                                isClearable={false}
                                isSearchable={false}
                                menuPosition="fixed"
                                isDisabled={isLoadingScenarios}
                                styles={{
                                    ...customStyles,
                                    control: styles => ({
                                        ...styles,
                                        border: 'none',
                                        backgroundColor: '#4F5A6A',
                                    }),
                                }}
                            />
                        </RenderIf>
                    </NSCard>
                </RenderIf>

                {proFormaKeyStats.map((statRow, rowIndex) => renderKeyStatRow(statRow, rowIndex))}

                <RenderIf isTrue={showPartnerReturns}>
                    {proFormaPartnerReturnKeyStats.map((statRow, rowIndex) => renderKeyStatRow(statRow, `partner-${rowIndex}`))}
                </RenderIf>

                <RenderIf isTrue={!signUpMode}>
                    <div className="ProFormaSideNav__footer__buttons-container mt-2 d-flex align-items-center p-0">
                        <ProFormaSideNavSettingsMenu
                            showPartnerReturns={showPartnerReturns}
                            handleToggleShowPartnerReturns={handleToggleShowPartnerReturns}
                        />
                        <NSButton
                            id="export-pro-forma-btn"
                            icon={IconDownload}
                            className="mr-1 NSButton--secondary-lighten"
                            ariaLabel="Export pro forma to Excel"
                            callback={handleExportToExcel}
                            disabled={isExporting || !enabledForExport}
                            disabledTooltip={!enabledForExport ? 'Excel export coming soon!' : undefined}
                        />
                        <NSButton
                            id="share-pro-forma-btn"
                            ariaLabel="Share pro forma"
                            text={isCollapsed ? '' : 'Share pro forma'}
                            icon={IconShare}
                            className={`w-100 NSButton--secondary-lighten ${!isCollapsed ? 'text-truncate' : ''}`}
                            callback={() => setIsShareProFormaModalOpen(true)}
                        />

                    </div>
                    <RenderIf isTrue={proForma.subprojectId == null}>
                        <div className="ProFormaSideNav__footer__buttons-container mt-2">
                            <NSButton
                                id="link-or-create-project-btn"
                                ariaLabel="Link or create project"
                                text={isCollapsed ? '' : 'Link or create project'}
                                className={`w-100 heap-deal-pro-forma-create-project-from-deal ${!isCollapsed ? 'text-truncate' : ''}`}
                                icon={IconRocket}
                                color="primary"
                                callback={() => setIsCreateProjectModalOpen(true)}
                            />
                        </div>
                    </RenderIf>
                </RenderIf>
            </NSCard>
            <CreateProjectModal
                toggle={() => setIsCreateProjectModalOpen(false)}
                isOpen={isCreateProjectModalOpen}
            />
            {isUnlinkProjectModalOpen && (
                <UnlinkProjectModal
                    isOpen={isUnlinkProjectModalOpen}
                    toggle={() => setIsUnlinkProjectModalOpen(false)}
                    dealId={proForma.dealId}
                    proFormaId={proForma.id}
                    onUnlinked={() => updateProFormaLocal({ subprojectId: null, projectBudgetScenarioId: null })}
                />
            )}
            {isShareProFormaModalOpen && (
                <ShareProFormaModal
                    isOpen={isShareProFormaModalOpen}
                    toggle={() => setIsShareProFormaModalOpen(false)}
                    proForma={proForma}
                />
            )}
        </>

    );
};

export default ProFormaSideNavFooter;
